import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, todayLocal, EXPENSE_PAYMENT_METHODS } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { ObraInvoicesTab } from "@/components/ObraInvoicesTab";
import { SatProductsTab } from "@/components/SatProductsTab";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Loader2, Search, DollarSign, Receipt, CloudDownload } from "lucide-react";
import { toast } from "sonner";

const ST = {
  pendiente: { label: "Pendiente", color: "#f59e0b" },
  parcial: { label: "Parcial", color: "#22d3ee" },
  pagada: { label: "Pagada", color: "#16a34a" },
  cancelada: { label: "Cancelada", color: "#dc2626" },
};
const FORMS = [["01", "Efectivo"], ["03", "Transferencia"], ["04", "T. crédito"], ["28", "T. débito"], ["02", "Cheque"], ["99", "Por definir"]];

export default function Invoicing() {
  const { user } = useAuth();
  const [tab, setTab] = useState("ventas");
  const [sales, setSales] = useState([]);
  const [pending, setPending] = useState([]);
  const [stats, setStats] = useState(null);
  const [openNew, setOpenNew] = useState(false);
  const [payFor, setPayFor] = useState(null);

  const load = async () => {
    const [{ data: s }, { data: p }] = await Promise.all([api.get("/sales-invoices"), api.get("/invoices-pending")]);
    setSales(s); setPending(p);
  };
  const loadStats = async () => { try { const { data } = await api.get("/billing/facturapi-stats"); setStats(data); } catch { /* noop */ } };
  useEffect(() => { load(); loadStats(); }, []);

  const stamp = async (inv) => {
    if (!window.confirm(`¿TIMBRAR ${inv.folio} ante el SAT (${fmtMXN(inv.total)})?`)) return;
    try {
      const { data } = await api.post(`/client-invoices/${inv.id}/stamp`);
      toast.success(`Timbrada · UUID ${data.uuid_cfdi || "pendiente"}${data.cfdi_test_mode ? " (TEST)" : ""}`);
      load(); loadStats();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al timbrar"); }
  };
  const dlCfdi = async (inv, kind) => {
    try {
      const res = await api.get(`/client-invoices/${inv.id}/cfdi/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `cfdi_${inv.folio.replace(" ", "")}.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar"); }
  };
  const emailCfdi = async (inv) => {
    const email = window.prompt(`Enviar factura ${inv.folio} (XML + PDF) por correo.\nDeja vacío para usar el correo registrado del cliente:`, "");
    if (email === null) return;
    try {
      const { data } = await api.post(`/client-invoices/${inv.id}/send-email`, { email: email || null });
      toast.success(`Factura enviada a ${data.sent_to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
  };
  const cancelCfdi = async (inv) => {
    const motive = window.prompt("Motivo SAT: 01 con relación · 02 sin relación · 03 no realizada · 04 nominativa", "02");
    if (!motive) return;
    let substitution = null;
    if (motive === "01") { substitution = window.prompt("UUID sustituto:"); if (!substitution) return; }
    try { await api.post(`/client-invoices/${inv.id}/cfdi-cancel`, { motive, substitution }); toast.success("Cancelación enviada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const InvRow = (inv, showType = false) => {
    const st = ST[inv.status] || ST.pendiente;
    return (
      <div key={inv.id} className="panel px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`sale-invoice-${inv.id}`}>
        <span className="font-mono text-sm font-bold text-slate-100">{inv.folio}</span>
        <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}66` }}>{st.label}</span>
        {showType && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(34,211,238,0.1)", color: "var(--cyan)" }}>{inv.invoice_type || "obra"}</span>}
        <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
          {fmtDate(inv.invoice_date)} · {inv.customer_name || inv.concept}
          {inv.uuid_cfdi && <span className="ml-2 font-mono" style={{ color: "#86efac" }}>UUID …{inv.uuid_cfdi.slice(-8)}{inv.cfdi_test_mode ? " · TEST" : ""}</span>}
        </div>
        <div className="text-right">
          <div className="font-mono text-sm font-bold text-slate-100">{fmtMXN(inv.total)}</div>
          {inv.balance > 0.01 && <div className="text-[10px]" style={{ color: "#f59e0b" }}>saldo {fmtMXN(inv.balance)}</div>}
        </div>
        <div className="flex items-center gap-1">
          {!inv.uuid_cfdi && inv.status !== "cancelada" && (
            <button data-testid={`stamp-sale-${inv.id}`} onClick={() => stamp(inv)} className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">⚡ Timbrar</button>
          )}
          {inv.facturapi_id && (
            <>
              <button onClick={() => dlCfdi(inv, "xml")} className="btn-ghost px-2 py-1 text-[10px]" style={{ color: "#86efac", border: "1px solid #16a34a" }}>XML</button>
              <button onClick={() => dlCfdi(inv, "pdf")} className="btn-ghost px-2 py-1 text-[10px]" style={{ color: "#86efac", border: "1px solid #16a34a" }}>PDF</button>
              <button data-testid={`email-sale-${inv.id}`} onClick={() => emailCfdi(inv)} title={inv.emailed_at ? `Enviada a ${inv.emailed_to}` : "Enviar por correo"} className="btn-ghost px-2 py-1 text-[10px]" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>✉ {inv.emailed_at ? "Reenviar" : "Enviar"}</button>
              {inv.status !== "cancelada" && <button onClick={() => cancelCfdi(inv)} className="btn-ghost px-2 py-1 text-[10px]" style={{ color: "#fca5a5", border: "1px solid #dc2626" }}>Cancelar</button>}
            </>
          )}
          {inv.status !== "cancelada" && inv.balance > 0.01 && (
            <button data-testid={`sale-pay-${inv.id}`} onClick={() => setPayFor(inv)} className="btn-ghost px-2 py-1 text-[10px] uppercase" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>
              <DollarSign className="w-3 h-3 inline" />Cobro
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="invoicing-page">
      <PageHeader title="Facturación" subtitle="Ventas de material y facturas emitidas"
        actions={<button data-testid="new-sale-btn" onClick={() => setOpenNew(true)} className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2"><Plus className="w-4 h-4" /> Facturar venta</button>} />
      <div className="flex gap-2 mb-4">
        {[["ventas", "Facturar / Ventas"], ["obras", "Obras · Serie C"], ["productos", "Productos SAT"], ["pendientes", `Pendientes de cobro (${pending.length})`], ["sat", "Facturas SAT"]].map(([k, l]) => (
          <button key={k} data-testid={`invtab-${k}`} onClick={() => setTab(k)} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm"
            style={{ background: tab === k ? "rgba(34,211,238,0.12)" : "transparent", color: tab === k ? "var(--cyan)" : "var(--text-dim)", border: `1px solid ${tab === k ? "var(--cyan)" : "var(--border)"}` }}>{l}</button>
        ))}
      </div>

      {tab === "ventas" && (
        <div className="space-y-2">
          {sales.length === 0 && <div className="panel p-8 text-center text-slate-400 text-sm"><Receipt className="w-8 h-8 mx-auto mb-2 text-slate-600" />Sin ventas facturadas. Usa "Facturar venta" para vender tabique, arena, grava, etc.</div>}
          {sales.map((s) => InvRow(s))}
        </div>
      )}
      {tab === "obras" && <ObraInvoicesTab user={user} CobroModal={CobroModal} />}
      {tab === "productos" && <SatProductsTab user={user} />}
      {tab === "pendientes" && (
        <div className="space-y-2">
          {pending.length === 0 && <div className="panel p-8 text-center text-slate-400 text-sm">Sin facturas pendientes de cobro 🎉</div>}
          {pending.map((s) => InvRow(s, true))}
        </div>
      )}
      {tab === "sat" && (
        <div className="space-y-3" data-testid="sat-stats">
          {!stats ? <div className="panel p-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[["Timbradas ante SAT", stats.sat_total ?? "—", "#86efac"], ["Timbradas (local)", stats.local_timbradas, "var(--cyan)"], ["Facturas totales", stats.local_total, "#e2e8f0"], ["Modo", stats.test_mode ? "TEST" : "LIVE", stats.test_mode ? "#f59e0b" : "#86efac"]].map(([l, v, c]) => (
                  <div key={l} className="panel p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500">{l}</div><div className="font-mono text-lg font-bold" style={{ color: c }}>{v}</div></div>
                ))}
              </div>
              {stats.api_error && <div className="panel p-3 text-xs" style={{ color: "#fca5a5" }}>⚠️ API Facturapi: {stats.api_error}</div>}
              {stats.sat_recientes.length > 0 && (
                <div className="panel p-4">
                  <div className="label-mono mb-2">// ÚLTIMAS TIMBRADAS (FACTURAPI)</div>
                  {stats.sat_recientes.map((i) => (
                    <div key={i.id} className="flex items-center gap-3 text-xs py-1" style={{ borderTop: "1px solid var(--border)" }}>
                      <span className="font-mono text-slate-300">{i.series} {i.folio_number}</span>
                      <span className="text-slate-400 flex-1 truncate">{i.customer}</span>
                      <span className="font-mono text-slate-500">…{(i.uuid || "").slice(-8)}</span>
                      <span className="font-mono font-bold" style={{ color: "#86efac" }}>{fmtMXN(i.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {openNew && <SaleModal onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
      {payFor && <CobroModal inv={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }} />}
    </div>
  );
}

function SaleModal({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ contracting_entity_id: "", payment_method: "PUE", payment_form: "01", invoice_date: todayLocal() });
  const [items, setItems] = useState([{ description: "", product_key: "", unit_key: "H87", unit: "pza", quantity: 1, unit_price: "" }]);
  const [saving, setSaving] = useState(false);
  const [catQ, setCatQ] = useState("");
  const [catRes, setCatRes] = useState([]);
  const [catFor, setCatFor] = useState(null);

  useEffect(() => { api.get("/contracting-entities").then(({ data }) => setClients(data || [])); }, []);
  const searchCat = async () => {
    try { const { data } = await api.get(`/sat-catalog/products?q=${encodeURIComponent(catQ)}`); setCatRes(data.results || []); }
    catch { setCatRes([]); }
  };
  const pickCat = (r) => {
    if (catFor === null) return;
    setItems((its) => its.map((it, i) => i === catFor ? { ...it, product_key: r.key, description: it.description || r.description, unit_key: r.unit_key || it.unit_key } : it));
    setCatFor(null); setCatRes([]);
  };
  const total = items.reduce((s, i) => s + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/sales-invoices", {
        ...form,
        items: items.filter((i) => i.description && Number(i.unit_price) > 0).map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price), product_key: i.product_key || null })),
      });
      toast.success(`Venta ${data.folio} creada por ${fmtMXN(data.total)}`);
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl panel-strong max-h-[92vh] overflow-y-auto p-5 space-y-3" data-testid="sale-modal">
        <div className="label-mono">// FACTURAR VENTA DE MATERIAL · SERIE M</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><label className="label-mono block mb-1">Cliente *</label>
            <select required value={form.contracting_entity_id} onChange={(e) => setForm({ ...form, contracting_entity_id: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" data-testid="sale-client-select">
              <option value="" style={{ background: "#0a0f1d" }}>— Selecciona —</option>
              {clients.map((c) => <option key={c.id} value={c.id} style={{ background: "#0a0f1d" }}>{c.name} {c.rfc ? `(${c.rfc})` : "⚠ sin RFC"}</option>)}
            </select></div>
          <div><label className="label-mono block mb-1">Método de pago</label>
            <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value, payment_form: e.target.value === "PPD" ? "99" : "01" })} className="input-cyber px-3 py-2 text-sm w-full" data-testid="sale-method">
              <option value="PUE" style={{ background: "#0a0f1d" }}>PUE — Pago en una exhibición (mostrador)</option>
              <option value="PPD" style={{ background: "#0a0f1d" }}>PPD — Parcialidades / crédito</option>
            </select></div>
          <div><label className="label-mono block mb-1">Forma de pago</label>
            <select value={form.payment_form} onChange={(e) => setForm({ ...form, payment_form: e.target.value })} disabled={form.payment_method === "PPD"} className="input-cyber px-3 py-2 text-sm w-full">
              {FORMS.map(([v, l]) => <option key={v} value={v} style={{ background: "#0a0f1d" }}>{v} {l}</option>)}
            </select></div>
        </div>
        <div className="label-mono">// PARTIDAS</div>
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-4"><input required placeholder="Descripción (ej. Tabique rojo)" value={it.description} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} className="input-cyber px-2 py-2 text-xs w-full" data-testid={`item-desc-${idx}`} /></div>
            <div className="col-span-2 flex gap-1">
              <input placeholder="Clave SAT" value={it.product_key} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, product_key: e.target.value } : x))} className="input-cyber px-2 py-2 text-xs w-full font-mono" />
              <button type="button" title="Buscar en catálogo SAT" onClick={() => { setCatFor(idx); setCatQ(it.description); }} className="btn-ghost p-2"><Search className="w-3.5 h-3.5" /></button>
            </div>
            <div className="col-span-1"><input placeholder="pza" value={it.unit} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, unit: e.target.value } : x))} className="input-cyber px-2 py-2 text-xs w-full" /></div>
            <div className="col-span-2"><input required type="number" step="0.01" min="0.01" placeholder="Cant." value={it.quantity} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, quantity: e.target.value } : x))} className="input-cyber px-2 py-2 text-xs w-full text-right font-mono" data-testid={`item-qty-${idx}`} /></div>
            <div className="col-span-2"><input required type="number" step="0.01" min="0.01" placeholder="P. unit." value={it.unit_price} onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, unit_price: e.target.value } : x))} className="input-cyber px-2 py-2 text-xs w-full text-right font-mono" data-testid={`item-price-${idx}`} /></div>
            <div className="col-span-1">{items.length > 1 && <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="btn-ghost p-2" style={{ color: "#fca5a5" }}><Trash2 className="w-3.5 h-3.5" /></button>}</div>
          </div>
        ))}
        {catFor !== null && (
          <div className="panel p-3 space-y-2">
            <div className="flex gap-2">
              <input value={catQ} onChange={(e) => setCatQ(e.target.value)} placeholder="Buscar en catálogo SAT (ej. arena)" className="input-cyber px-2 py-1.5 text-xs flex-1" data-testid="cat-search-input" />
              <button type="button" onClick={searchCat} className="btn-primary px-3 py-1.5 text-xs" data-testid="cat-search-btn">Buscar</button>
              <button type="button" onClick={() => { setCatFor(null); setCatRes([]); }} className="btn-ghost px-2 py-1.5 text-xs">✕</button>
            </div>
            {catRes.map((r) => (
              <button type="button" key={r.key + r.description} onClick={() => pickCat(r)} className="block w-full text-left text-xs hover:bg-white/5 px-2 py-1 rounded-sm">
                <span className="font-mono" style={{ color: "var(--cyan)" }}>{r.key}</span> · {r.description}
              </button>
            ))}
          </div>
        )}
        <button type="button" onClick={() => setItems([...items, { description: "", product_key: "", unit_key: "H87", unit: "pza", quantity: 1, unit_price: "" }])} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest" data-testid="add-item-btn"><Plus className="w-3 h-3 inline" /> Partida</button>
        <div className="text-right text-sm">Subtotal <b className="font-mono">{fmtMXN(total)}</b> · IVA <b className="font-mono">{fmtMXN(total * 0.16)}</b> · <span className="text-slate-400">Total</span> <b className="font-mono" style={{ color: "#86efac" }}>{fmtMXN(total * 1.16)}</b></div>
        <div className="flex justify-end gap-2 pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving} data-testid="save-sale-btn" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50">{saving ? "Guardando…" : "Crear factura"}</button>
        </div>
      </form>
    </div>
  );
}

function CobroModal({ inv, onClose, onSaved }) {
  const [form, setForm] = useState({ amount: inv.balance, payment_date: todayLocal(), method: "transferencia", reference: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/client-invoices/${inv.id}/payments`, { ...form, amount: Math.round(Number(form.amount) * 100) / 100 });
      toast.success("Cobro registrado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)" }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm panel-strong p-5 space-y-3">
        <div className="label-mono">// COBRO · {inv.folio}</div>
        <input required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full text-right font-mono" />
        <input required type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full" />
        <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="input-cyber px-3 py-2 text-sm w-full">
          {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value} style={{ background: "#0a0f1d" }}>{m.label}</option>)}
        </select>
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase">Cancelar</button>
          <button type="submit" disabled={saving} className="btn-primary px-4 py-2 text-xs uppercase disabled:opacity-50">Registrar</button></div>
      </form>
    </div>
  );
}
